import { IssueFieldEnum } from "@mcapps/shared/enums";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  CloseButton,
  Code,
  Collapse,
  Divider,
  Flex,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconCircle, IconCircleCheck, IconFilter, IconInfoCircle, IconSend, IconSparkles } from "@tabler/icons-react";
import type { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useState, type RefObject } from "react";
import { useSelector } from "react-redux";
import { usePostProjectByProjectIdIssueboardAndIssueboardIdIssuesOnBoardAskAiMutation } from "../../../../../../openapi";
import { selectIsUserDev, setShowFailedMessageThunk } from "../../../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";

import {
  selectActiveIssueTableColumnFilterCount,
  selectIssueAskAiFilterOnCurrentBoard,
  selectIssueRegisterNameOptionOnBoard,
  selectIssuesOrTasksOnBoardArgs,
  selectNrOfFiltredIssues,
  selectTaskRegisterNameOptionOnBoard,
} from "../../issueSelectors";
import {
  clearIssueAskAiFilterInRedux,
  setIssueDropdownSingleDataFieldFilterInRedux,
  setIssueAskAiFilterResultInRedux,
  setMiniSearchFreeTextInRedux,
  setTaskAndOrIssueArgsInRedux,
} from "../../issueSlice";
import { clearIssueTableColumnFiltersThunk } from "../../issueThunk";
import { FilterDropdown } from "./FilterDropdown";
import {
  makeSelectActiveFiltersByField,
  makeSelectUniqueIssueFilterFromDataField,
  selectAssignedToHashmap,
  selectDisciplineFilter,
  selectIssueDatesFiler,
  selectMineSakerFilter,
} from "./issueFilterSelectors";

const priortyMap = {
  LOW: "Lav",
  MEDIUM: "Medium",
  HIGH: "Høy",
};

type IssueDbFilterBarNewProps = {
  gridRef: RefObject<AgGridReact>;
};

export const IssueDbFilterBarNew: React.FC<IssueDbFilterBarNewProps> = ({ gridRef }) => {
  const dispatch = useAppDispatch();

  // Memoized selectors
  const selectFilters = useMemo(makeSelectUniqueIssueFilterFromDataField, []);
  const selectActive = useMemo(makeSelectActiveFiltersByField, []);

  // Hämta alla filter
  const tagsFilters = useAppSelector((state) => selectFilters(state, IssueFieldEnum.TAGS, null));
  const priorityFilters = useAppSelector((state) => selectFilters(state, IssueFieldEnum.PRIORITY, priortyMap));
  const statusFilters = useAppSelector((state) => selectFilters(state, IssueFieldEnum.STATUS, null));
  const typeFilters = useAppSelector((state) => selectFilters(state, IssueFieldEnum.TYPEOFISSUE, null));
  const bucketFilters = useAppSelector((state) => selectFilters(state, IssueFieldEnum.BUCKET, null));
  const disciplineFilter = useSelector(selectDisciplineFilter);
  const assignedToHashmap = useAppSelector(selectAssignedToHashmap);
  const assignedToFilters = useAppSelector((state) =>
    selectFilters(state, IssueFieldEnum.ASSIGNED_TO_ID, assignedToHashmap),
  );
  const mineSakerFilter = useAppSelector(selectMineSakerFilter);
  const dueDateFilter = useAppSelector(selectIssueDatesFiler);

  // Aktiva filter
  const activeTags = useAppSelector((s) => selectActive(s, IssueFieldEnum.TAGS));
  const activePriority = useAppSelector((s) => selectActive(s, IssueFieldEnum.PRIORITY));
  const activeStatus = useAppSelector((s) => selectActive(s, IssueFieldEnum.STATUS));
  const activeAssignedTo = useAppSelector((s) => selectActive(s, IssueFieldEnum.ASSIGNED_TO_ID));
  const activeDueDate = useAppSelector((s) => selectActive(s, IssueFieldEnum.DATES_DUE));
  const activeDiscipline = useAppSelector((s) => selectActive(s, IssueFieldEnum.DISCIPLIN));
  const activeType = useAppSelector((s) => selectActive(s, IssueFieldEnum.TYPEOFISSUE));
  const activeBucket = useAppSelector((s) => selectActive(s, IssueFieldEnum.BUCKET));
  const activeMineSaker = useAppSelector((s) => selectActive(s, IssueFieldEnum.MINE_SAKER));

  const nrOfFilteredIssues = useAppSelector(selectNrOfFiltredIssues);
  const activeTableColumnFilterCount = useAppSelector(selectActiveIssueTableColumnFilterCount);
  const miniSearchFreeText = useAppSelector((s) => s.issueReducerNew.miniSearchFreeText);

  // === FILTER KONFIGURATIONER ===
  const filterConfigs = useMemo(
    () =>
      [
        {
          filter: mineSakerFilter,
          active: activeMineSaker,
          field: IssueFieldEnum.MINE_SAKER,
        },
        {
          filter: statusFilters,
          active: activeStatus,
          field: IssueFieldEnum.STATUS,
        },
        {
          filter: dueDateFilter,
          active: activeDueDate,
          field: IssueFieldEnum.DATES_DUE,
        },
        {
          filter: bucketFilters,
          active: activeBucket,
          field: IssueFieldEnum.BUCKET,
        },
        {
          filter: tagsFilters,
          active: activeTags,
          field: IssueFieldEnum.TAGS,
        },
        {
          filter: typeFilters,
          active: activeType,
          field: IssueFieldEnum.TYPEOFISSUE,
        },
        {
          filter: assignedToFilters,
          active: activeAssignedTo,
          field: IssueFieldEnum.ASSIGNED_TO_ID,
        },
        {
          filter: disciplineFilter,
          active: activeDiscipline,
          field: IssueFieldEnum.DISCIPLIN,
        },
        {
          filter: priorityFilters,
          active: activePriority,
          field: IssueFieldEnum.PRIORITY,
        },
      ].filter((config) => config.filter?.options != null),
    [
      mineSakerFilter,
      tagsFilters,
      priorityFilters,
      dueDateFilter,
      statusFilters,
      assignedToFilters,
      disciplineFilter,
      typeFilters,
      bucketFilters,
      activeMineSaker,
      activeTags,
      activePriority,
      activeDueDate,
      activeStatus,
      activeAssignedTo,
      activeDiscipline,
      activeType,
      activeBucket,
    ],
  );

  const handleFilterChange = useCallback(
    (filterKey: IssueFieldEnum, values: string[]) => {
      dispatch(
        setIssueDropdownSingleDataFieldFilterInRedux({
          filter: filterKey,
          values,
        }),
      );
    },
    [dispatch],
  );

  const clearActiveTableColumnFilters = useCallback(() => {
    gridRef.current?.api?.setFilterModel(null);
    dispatch(clearIssueTableColumnFiltersThunk());
  }, [dispatch, gridRef]);

  return (
    <Card mx="xxs" p="xs" withBorder radius="sm" bg={"#fafafb"} bd={"1px solid var(--mantine-color-warmgray-2)"}>
      <Group justify="space-between" wrap="nowrap" gap="0">
        <Flex rowGap="xxs" columnGap={"0.7em"} wrap={"wrap"} align="center">
          <TaskIssueToggle />
          <TextInput
            placeholder="Søk..."
            value={miniSearchFreeText}
            onChange={(e) => dispatch(setMiniSearchFreeTextInRedux(e.currentTarget.value))}
            size="sm"
            w={150}
            miw={120}
            rightSection={
              miniSearchFreeText?.length > 0 && (
                <CloseButton size="sm" onClick={() => dispatch(setMiniSearchFreeTextInRedux(""))} title="Tøm søk" />
              )
            }
          />

          {/* LOOPA ÖVER ALLA FILTER */}
          {filterConfigs.map(({ filter, active, field }) => (
            <FilterDropdown
              key={field}
              filter={filter}
              value={active}
              onChange={(key, values) => handleFilterChange(key as IssueFieldEnum, values)}
              enableSearch
            />
          ))}
        </Flex>
        <Group miw={"40px"} justify="end" align="center" gap="xs" wrap="nowrap">
          <IssueAskAiFilterInput />
          {activeTableColumnFilterCount > 0 && (
            <Group gap={2} wrap="nowrap">
              <Tooltip label={`${activeTableColumnFilterCount} aktive tabellfiltere`}>
                <Badge
                  aria-label={`${activeTableColumnFilterCount} aktive tabellfiltere`}
                  color="yellow"
                  leftSection={<IconFilter size={12} />}
                  size="sm"
                  variant="light"
                  tt="none"
                >
                  {activeTableColumnFilterCount}
                </Badge>
              </Tooltip>
              <Tooltip label="Fjern tabellfiltere">
                <CloseButton
                  size="sm"
                  onClick={clearActiveTableColumnFilters}
                  title="Fjern tabellfiltere"
                  aria-label="Fjern tabellfiltere"
                />
              </Tooltip>
            </Group>
          )}
          <Badge color="green" size="sm" variant="filled">
            {nrOfFilteredIssues}
          </Badge>
        </Group>
      </Group>
    </Card>
  );
};

const IssueAskAiFilterInput: React.FC = () => {
  const dispatch = useAppDispatch();
  const args = useAppSelector(selectIssuesOrTasksOnBoardArgs);
  const activeFilter = useAppSelector(selectIssueAskAiFilterOnCurrentBoard);
  const isUserDev = useAppSelector(selectIsUserDev);
  const [askAi, { isLoading }] = usePostProjectByProjectIdIssueboardAndIssueboardIdIssuesOnBoardAskAiMutation();
  const [isOpen, setIsOpen] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [isDebugOpen, setIsDebugOpen] = useState(false);
  const [unsupportedQuestion, setUnsupportedQuestion] = useState<string | null>(null);
  const [unsupportedMessage, setUnsupportedMessage] = useState<string | null>(null);
  const [question, setQuestion] = useState(activeFilter?.question ?? "");

  const close = () => {
    setIsOpen(false);
    setQuestion(activeFilter?.question ?? "");
  };

  const clear = () => {
    dispatch(clearIssueAskAiFilterInRedux());
    setQuestion("");
    setIsOpen(false);
  };

  const submit = async () => {
    const trimmedQuestion = question.trim();
    if (trimmedQuestion.length === 0 || args.projectId == null || args.issueboardId == null) {
      return;
    }

    try {
      const result = await askAi({
        projectId: args.projectId,
        issueboardId: args.issueboardId,
        body: {
          question: trimmedQuestion,
          includeIssues: args.includeIssues !== "false",
          includeTasks: args.includeTasks !== "false",
          includeAllCompletedIssues: args.includeAllCompletedIssues === "true",
          taskOrIssue: args.taskOrIssue,
        },
      }).unwrap();

      if (result.unsupported != null) {
        setUnsupportedQuestion(trimmedQuestion);
        setUnsupportedMessage(result.unsupported.message);
        setIsOpen(false);
        return;
      }

      dispatch(
        setIssueAskAiFilterResultInRedux({
          projectId: args.projectId,
          issueboardId: args.issueboardId,
          question: trimmedQuestion,
          issues: result.issues,
          explanation: result.explanation,
          debug: result.debug,
        }),
      );
      setIsOpen(false);
    } catch (error) {
      const message =
        (error as { data?: { message?: string }; message?: string } | undefined)?.data?.message ??
        (error as { message?: string } | undefined)?.message ??
        "Kunne ikke filtrere med AI";
      dispatch(setShowFailedMessageThunk(message));
    }
  };

  const unsupportedModal = (
    <Modal
      opened={unsupportedMessage != null}
      onClose={() => {
        setUnsupportedMessage(null);
        setUnsupportedQuestion(null);
      }}
      title="AI-spørsmål"
      size="md"
      yOffset={120}
      radius="md"
    >
      <Stack gap="sm">
        {unsupportedQuestion != null && (
          <div>
            <Text size="sm" c="dimmed">
              Spørsmål
            </Text>
            <Text size="sm" fw={600}>
              {unsupportedQuestion}
            </Text>
          </div>
        )}
        <Text size="sm">{unsupportedMessage ?? "Denne typen spørsmål støttes ikke ennå."}</Text>
        <Text size="sm" c="dimmed">
          Foreløpig kan AI-feltet bare brukes til å filtrere listen, for eksempel saker med tidsfrist neste uke, åpne
          saker eller saker med en bestemt ansvarlig.
        </Text>
      </Stack>
    </Modal>
  );

  const infoModal = activeFilter != null && (
    <Modal
      opened={isInfoOpen}
      onClose={() => setIsInfoOpen(false)}
      title="AI-filter"
      size="lg"
      yOffset={120}
      radius="md"
    >
      <Stack gap="sm">
        <div>
          <Text size="sm" c="dimmed">
            Spørsmål
          </Text>
          <Text size="sm" fw={600}>
            {activeFilter.question}
          </Text>
        </div>

        {activeFilter.explanation != null ? (
          <>
            <div>
              <Text size="sm" c="dimmed">
                Tolkning
              </Text>
              <Text size="sm">{activeFilter.explanation.summary}</Text>
            </div>

            {activeFilter.explanation.filters.length > 0 && (
              <div>
                <Text size="sm" c="dimmed" mb={4}>
                  Filter brukt
                </Text>
                <Stack gap={4}>
                  {activeFilter.explanation.filters.map((filter, index) => (
                    <Text key={`${filter}-${index}`} size="sm">
                      {filter}
                    </Text>
                  ))}
                </Stack>
              </div>
            )}

            <Group gap="xs">
              {activeFilter.explanation.sort != null && (
                <Badge color="gray" variant="light">
                  Sortering: {activeFilter.explanation.sort}
                </Badge>
              )}
              {activeFilter.explanation.limit != null && (
                <Badge color="gray" variant="light">
                  Maks {activeFilter.explanation.limit}
                </Badge>
              )}
              <Badge color="violet" variant="light">
                Sikkerhet {Math.round(activeFilter.explanation.confidence * 100)}%
              </Badge>
            </Group>
          </>
        ) : (
          <Text size="sm">Ingen AI-tolkning er lagret for dette filteret.</Text>
        )}

        {isUserDev && activeFilter.debug != null && (
          <>
            <Divider />
            <Button
              size="xs"
              variant="subtle"
              color="gray"
              onClick={() => setIsDebugOpen((value) => !value)}
              w="fit-content"
            >
              {isDebugOpen ? "Skjul teknisk debug" : "Vis teknisk debug"}
            </Button>
            <Collapse in={isDebugOpen}>
              <Code block style={{ maxHeight: 320, overflow: "auto", whiteSpace: "pre-wrap" }}>
                {JSON.stringify(activeFilter.debug, null, 2)}
              </Code>
            </Collapse>
          </>
        )}
      </Stack>
    </Modal>
  );

  if (!isOpen) {
    if (activeFilter != null) {
      return (
        <>
          <Group gap="4px" wrap="nowrap">
            <Button
              size="sm"
              px="xs"
              color="violet"
              variant="filled"
              leftSection={<IconSparkles size={16} />}
              maw={360}
              onClick={() => {
                setQuestion(activeFilter.question);
                setIsOpen(true);
              }}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                AI (beta): {activeFilter.question}
              </span>
            </Button>
            <Tooltip label="Vis hvordan AI filtrerte">
              <ActionIcon size="sm" variant="light" color="violet" onClick={() => setIsInfoOpen(true)}>
                <IconInfoCircle size={16} />
              </ActionIcon>
            </Tooltip>
            <CloseButton size="sm" onClick={clear} title="Tøm AI-filter" />
          </Group>
          {infoModal}
          {unsupportedModal}
        </>
      );
    }

    return (
      <>
        <Button
          size="sm"
          px="xs"
          color="violet"
          variant="light"
          leftSection={<IconSparkles size={16} />}
          onClick={() => {
            setQuestion("");
            setIsOpen(true);
          }}
        >
          Spør AI (beta)
        </Button>
        {unsupportedModal}
      </>
    );
  }

  return (
    <>
      <Group gap="xs" wrap="nowrap">
        <TextInput
          autoFocus
          placeholder="F.eks. åpne saker med frist neste uke"
          value={question}
          onChange={(event) => setQuestion(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void submit();
            }
            if (event.key === "Escape") {
              close();
            }
          }}
          size="sm"
          w={360}
          miw={240}
          leftSection={<IconSparkles size={16} />}
          rightSection={question.length > 0 && <CloseButton size="sm" onClick={() => setQuestion("")} title="Tøm" />}
        />
        <Tooltip label="Filtrer med AI">
          <Button
            size="sm"
            px="xs"
            loading={isLoading}
            disabled={question.trim().length === 0 || args.projectId == null || args.issueboardId == null}
            leftSection={<IconSend size={16} />}
            onClick={() => void submit()}
          >
            Send
          </Button>
        </Tooltip>
        <CloseButton size="sm" onClick={close} title="Lukk AI-filter" />
      </Group>
      {unsupportedModal}
    </>
  );
};

// TaskIssueToggle förblir oförändrad
export const TaskIssueToggle: React.FC = () => {
  const dispatch = useAppDispatch();
  const selected = useAppSelector((s) => s.issueReducerNew.taskAndOrIssueArgs);

  const isTask = selected.includes("TASK");
  const isIssue = selected.includes("ISSUE");

  const update = useCallback(
    (type: "TASK" | "ISSUE") => {
      let next: ("TASK" | "ISSUE")[];
      if (selected.includes(type)) {
        next = selected.filter((t) => t !== type);
      } else {
        next = [...selected, type];
      }
      if (next.length === 0) {
        next = ["TASK", "ISSUE"];
      }
      dispatch(setTaskAndOrIssueArgsInRedux(next));
    },
    [selected, dispatch],
  );

  const issueNameOption = useAppSelector(selectIssueRegisterNameOptionOnBoard);
  const taskNameOption = useAppSelector(selectTaskRegisterNameOptionOnBoard);

  return (
    <Group gap="2px" wrap="nowrap">
      <Button
        style={{ borderRadius: "6px 0 0 6px" }}
        size="sm"
        px="xs"
        color={isIssue ? "mc-primary" : "warmgray.6"}
        variant={isIssue ? "filled" : "light"}
        onClick={() => update("ISSUE")}
        rightSection={isIssue ? <IconCircleCheck size={18} /> : <IconCircle size={18} />}
      >
        {issueNameOption ? issueNameOption.singular : "Sak"}
      </Button>
      <Button
        style={{ borderRadius: "0 6px 6px 0" }}
        size="sm"
        px="xs"
        color={isTask ? "mc-primary" : "warmgray.6"}
        variant={isTask ? "filled" : "light"}
        onClick={() => update("TASK")}
        rightSection={isTask ? <IconCircleCheck size={18} /> : <IconCircle size={18} />}
      >
        {taskNameOption ? taskNameOption.singular : "Aksjon"}
      </Button>
    </Group>
  );
};
